import { useEffect, useState } from "react";

interface ShareDialogProps {
  open: boolean;
  link: string;
  title: string;
  onClose: () => void;
}

const channels = [
  { key: "wechat", label: "微信", icon: "ri-wechat-line" },
  { key: "weibo", label: "微博", icon: "ri-weibo-line" },
  { key: "qq", label: "QQ", icon: "ri-qq-line" },
  { key: "link", label: "复制链接", icon: "ri-link" },
];

export default function ShareDialog({ open, link, title, onClose }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // clipboard may be unavailable; still show the demo feedback
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background-50/80 backdrop-blur-sm px-4 hn-fade"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-background-100 border border-background-200/70 rounded-lg p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-heading font-bold text-foreground-50">分享给朋友</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-50 hover:bg-background-200 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <p className="text-sm text-foreground-500 mb-5 line-clamp-1">《{title}》— 热情的邻居</p>

        <div className="grid grid-cols-4 gap-3">
          {channels.map((channel) => (
            <button
              key={channel.key}
              type="button"
              onClick={handleCopy}
              className="flex flex-col items-center gap-2 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-500/60 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
                <i className={`${channel.icon} text-lg`} />
              </span>
              <span className="text-xs text-foreground-300 whitespace-nowrap">{channel.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-5">
          <label className="text-xs text-foreground-500">分享链接</label>
          <div className="mt-2 flex items-center gap-2 bg-background-50 border border-background-200/60 rounded-md px-3 h-10">
            <input
              readOnly
              value={link}
              className="flex-1 bg-transparent outline-none text-sm text-foreground-200 truncate"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs text-accent-500 hover:text-accent-400 whitespace-nowrap cursor-pointer"
            >
              {copied ? "已复制" : "复制"}
            </button>
          </div>
          <p className="text-[11px] text-foreground-600 mt-2">分享为演示功能，不会真正发送到社交平台。</p>
        </div>
      </div>
    </div>
  );
}